import type { SxProps, Theme } from '@mui/material/styles';

export const coursePageStackSx: SxProps<Theme> = {
  width: '100%',
};

/** Courses hub shell — wider than default list pages so preview boards can scale up. */
export const courseHubShellSx: SxProps<Theme> = {
  maxWidth: {
    xs: '100%',
    lg: 1280,
    xl: 1600,
    xxl: 2100,
    xxxl: 2600,
  },
  px: { xs: 2, sm: 3, xxl: 5, xxxl: 6 },
};

/** Courses hub: stacked on mobile/tablet, Browse | Your courses side-by-side on lg+ (laptop). */
export const courseHubPanelsSx: SxProps<Theme> = {
  ...coursePageStackSx,
  flexDirection: { xs: 'column', lg: 'row' },
  alignItems: { lg: 'flex-start' },
  gap: { xs: 3, lg: 3, xxl: 3.5, xxxl: 4 },
};

export const courseHubPanelColumnSx: SxProps<Theme> = {
  width: '100%',
  minWidth: 0,
  flex: { lg: '1 1 0' },
};

/** Courses hub Browse: two phases per row on tablet landscape; one per row on phone and laptop+. */
export const coursePhaseBrowseGridSx: SxProps<Theme> = {
  width: '100%',
  display: 'grid',
  gridTemplateColumns: {
    xs: '1fr',
    md: 'repeat(2, minmax(0, 1fr))',
    lg: '1fr',
  },
  gap: { xs: 0, md: 2, lg: 0 },
  overflow: 'hidden',
};

/** Courses hub Browse grid — no outer border; rows use hover only. */
export const coursePhaseBrowseContainerSx: SxProps<Theme> = coursePhaseBrowseGridSx;

export const courseListItemShellSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  py: { xs: 1.5, sm: 2.25, xxl: 3 },
  px: { xs: 2, sm: 2.5, xxl: 3 },
  transition: 'background-color 0.15s ease',
  '@media (hover: hover) and (pointer: fine)': {
    '&:hover': {
      bgcolor: 'action.hover',
    },
  },
};

export const courseListItemVisualSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  gap: { xs: 1.25, sm: 1.5, xxl: 1.75 },
  width: '100%',
};

export const coursePhaseBrowseItemShellSx: SxProps<Theme> = courseListItemShellSx;

/** Course selection grids — matches dashboard RecentCoursesSection breakpoints. */
export const courseSelectionGridSx = (courseCount: number): SxProps<Theme> => ({
  width: '100%',
  display: 'grid',
  gridTemplateColumns:
    courseCount > 1
      ? { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }
      : '1fr',
  gap: 1.5,
});

export const courseDrilldownBrowseItemSx: SxProps<Theme> = {
  width: '100%',
  border: 1,
  borderColor: 'divider',
  borderRadius: 1,
  overflow: 'hidden',
};

export const courseDrilldownListItemShellSx: SxProps<Theme> = [
  courseListItemShellSx,
  courseDrilldownBrowseItemSx,
];

export const courseListPrimaryTypographySx: SxProps<Theme> = {
  fontSize: {
    xs: '1rem',
    sm: '1.0625rem',
    xxl: '1.125rem',
    xxxl: '1.3125rem',
  },
  fontWeight: 500,
  lineHeight: 1.35,
};

export const courseListSecondaryTypographySx: SxProps<Theme> = {
  fontSize: {
    xs: '0.8125rem',
    sm: '0.875rem',
    xxl: '1rem',
    xxxl: '1.0625rem',
  },
  mt: 0.5,
};

export const courseSectionHeadingSx: SxProps<Theme> = {
  fontSize: { xs: '0.875rem', xxl: '1rem', xxxl: '1.125rem' },
  fontWeight: 600,
};

export const coursePageTitleSx: SxProps<Theme> = {
  fontWeight: 600,
  fontSize: { xs: '1.25rem', sm: '1.5rem', xxl: '1.75rem', xxxl: '2rem' },
};

/** Opening White/Black side headings — nearly page-title size. */
export const courseOpeningSideHeadingSx: SxProps<Theme> = {
  display: 'block',
  fontWeight: 600,
  fontSize: { xs: '1.125rem', sm: '1.375rem', xxl: '1.625rem', xxxl: '1.875rem' },
  letterSpacing: 1,
  textTransform: 'uppercase',
  lineHeight: 1.2,
};

export const coursePageSubtitleSx: SxProps<Theme> = {
  mt: 0.5,
  fontSize: { xs: '0.875rem', xxl: '1rem', xxxl: '1.125rem' },
};

export const courseLessonPrimaryTypographySx: SxProps<Theme> = {
  fontSize: { xs: 13, sm: 14, xxl: 17, xxxl: 20 },
  fontWeight: 500,
  lineHeight: 1.4,
  wordBreak: 'break-word',
  pr: { xs: 1, sm: 0 },
};

export const courseLessonSecondaryTypographySx: SxProps<Theme> = {
  fontSize: { xs: 11, sm: 12, xxl: 15, xxxl: 17 },
  mt: 0.35,
};

export const courseSectionTitleSx: SxProps<Theme> = {
  fontWeight: 600,
  fontSize: { xs: '0.875rem', sm: '0.9375rem', xxl: '1.0625rem', xxxl: '1.25rem' },
  lineHeight: 1.35,
};

export const courseSectionEcoSx: SxProps<Theme> = {
  fontSize: { xs: '0.75rem', sm: '0.8125rem', xxl: '0.9375rem', xxxl: '1.0625rem' },
  maxWidth: { xs: 220, xxxl: 360 },
};

export const courseLessonRowSx: SxProps<Theme> = {
  alignItems: 'flex-start',
  py: { xs: 1, sm: 1.5, xxl: 1.75, xxxl: 2 },
};

export const courseDetailSectionsGridSx: SxProps<Theme> = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', xxxl: '1fr 1fr' },
  gap: { xs: 2, xxl: 2.5, xxxl: 3 },
  width: '100%',
};

export const courseDetailChipSx: SxProps<Theme> = {
  fontSize: { xs: '0.75rem', xxl: '0.8125rem', xxxl: '0.9375rem' },
  height: { xs: 24, xxl: 28, xxxl: 32 },
};

export const courseTrainerInfoSx: SxProps<Theme> = {
  fontSize: { xs: 14, lg: 15, xl: 16, xxl: 17, xxxl: 19 },
  width: '100%',
};

export const courseTrainerTitleSx: SxProps<Theme> = {
  fontWeight: 700,
  lineHeight: 1.25,
  minWidth: 0,
  fontSize: {
    xs: '1.125rem',
    lg: '1.25rem',
    xl: '1.375rem',
    xxl: '1.5rem',
    xxxl: '1.75rem',
  },
};

export const courseTrainerSubtitleSx: SxProps<Theme> = {
  fontSize: { xs: '0.8125rem', lg: '0.875rem', xxl: '0.9375rem', xxxl: '1rem' },
};

export const courseTrainerBackButtonSx: SxProps<Theme> = {
  flexShrink: 0,
  fontSize: { lg: '0.875rem', xxl: '0.9375rem', xxxl: '1rem' },
};

export const courseTrainerStatusSx: SxProps<Theme> = {
  textAlign: 'center',
  fontSize: {
    xs: '0.875rem',
    lg: '0.9375rem',
    xl: '1rem',
    xxl: '1.0625rem',
    xxxl: '1.125rem',
  },
};

export const courseTrainerFeedbackSx: SxProps<Theme> = {
  fontWeight: 600,
  lineHeight: 1.25,
  fontSize: {
    xs: '0.9375rem',
    lg: '1rem',
    xxl: '1.0625rem',
    xxxl: '1.125rem',
  },
};

export const courseTrainerScrubIconSx = {
  fontSize: { xs: 20, lg: 22, xxl: 26, xxxl: 28 },
} as const;
